import { DrawingToolHelpers } from '../../helpers/drawing-tool-helpers';
import { StringHelpers } from '../../helpers/string-helpers';
import { TraceLoggingHelpers } from '../../helpers/trace-logging-helpers';
import { IChartContainer } from "../../models/base/chart-container.interface";
import { DrawingToolsSettingModel } from '../../models/drawing-tools/drawing-tools-setting.model';
import { DrawingToolsType } from "../../models/enum/drawing-tools-type.enum";
import { Timeframe } from '../../models/enum/timeframe.enum';
import { IComponent } from "../component.interface";
import { FibonacciTimezonesComponent } from './Advanced/fibonacci-timezones.component';
import { FibonacciComponent } from './Advanced/fibonacci.component';
import { ParallelChannelComponent } from './Advanced/parallel-channel.component';
import { PitchforkComponent } from './Advanced/pitchfork.component';
import { TimeCyclesComponent } from './Advanced/time-cycles.component';
import { EmojiComponent } from './Emoji/emoji.component';
import { FlagsComponent } from './Flags/flags.component';
import { MeasureComponent } from './Measure/measure.component';
import { VerticalArrowComponent } from './Vertical/vertical-arrow.component';
import { VerticalCounterComponent } from './Vertical/vertical-counter.component';
import { VerticalLabelComponent } from './Vertical/vertical-label.component';
import { ArrowLineComponent } from './arrow-line.component';
import { ArrowRayComponent } from './arrow-ray.component';
import { ArrowSegmentComponent } from './arrow-segment.component';
import { Crooked3Component } from './crooked3.component';
import { Crooked5Component } from './crooked5.component';
import { CurvedLineComponent } from './curved-line.component';
import { IDrawingTool } from './drawing-tool.interface';
import { Elliot3LineComponent } from './elliot-3-line.component';
import { Elliot5LineComponent } from './elliot-5-line.component';
import { HorizontalLineComponent } from './horizontal-line.component';
import { LineComponent } from './line.component';
import { NewTextBoxComponent } from "./new-textbox.component";
import { RayComponent } from './ray.component';
import { SegmentComponent } from './segment.component';
import { VerticalLineComponent } from './vertical-line.component';

export class DrawingToolsManager implements IComponent {
    public id = StringHelpers.newGuid();

    public base: IChartContainer;
    public editAnnotaionPopupOpen = false;
    public emojiAddingMode = false;

    private symbol = '';
    private timeframe = Timeframe.OneMinute;

    private drawingToolsComponents: Array<IDrawingTool> = [];
    private cachedDrawingToolsToLoadJson: Array<any> = [];
    public drawingToolsSetting = new DrawingToolsSettingModel();
    private preSelectedEmoji = '';

    constructor(base: IChartContainer) {
        this.base = base;
        this.init();
        this.subscribeDrawingTools();
    }

    private init() {
        if (this.base.pnpMode) {
            return;
        }

        this.base.domEvents.onDeletePress(this.id, () => {
            const chart: any = this.base.tiChartSeries?.highStockChart;
            if (!chart) return;

            if (chart.navigationBindings.popup) {
                const removeButton = chart.navigationBindings.popup.container?.querySelector('.highcharts-annotation-remove-button');
                if (removeButton) {
                    removeButton.click();

                    chart.navigationBindings.popup.closePopup();
                }
            }

            if (chart.navigationBindings.activeAnnotation) {
                chart.navigationBindings.activeAnnotation.remove();
            }
        });

        this.base.domEvents.onMouseDown(this.id, (e: any) => {
            if (e && e.target) {
                const isAnnotation = DrawingToolHelpers.isDrawingToolAnnotation(e.target);
                TraceLoggingHelpers.log('isAnnotation ', [isAnnotation, e.target])
                if (isAnnotation) {
                    this.base.tiChartSeries.preventDragging = true;
                    TraceLoggingHelpers.log('highChartDrawingToolsEvent onMouseDown', e)
                }
            }
        });

        this.base.domEvents.onMouseUp(this.id, (e: any) => {
            if (this.base.tiChartSeries?.preventDragging) {
                this.base.tiChartSeries.preventDragging = false;
            }
        });
    }

    public subscribeForPopupChange() {
        this.base.tiChartSeries?.subscribePopupChange(this.id, (open: boolean, event: any) => {
            const popup = document.body.findFirst('.highcharts-popup:not(.custom-highchart-popup)');

            if (open) {
                this.selectedEmojiResizeState(event);

                if (popup && !popup.classList.contains('highcharts-annotation-toolbar')) {
                    this.editAnnotaionPopupOpen = true;
                }
            } else {
                this.editAnnotaionPopupOpen = false;

                this.unSelectEmojiResizeState()
            }
        });
    }

    private selectedEmojiResizeState(event: any) {
        this.unSelectEmojiResizeState()

        const id = event.annotation?.options?.id;
        if (!id) {
            return;
        }

        for (const comp of this.drawingToolsComponents) {
            if (comp instanceof EmojiComponent && comp.id == id) {
                comp.setComponentResizer(false);
                break;
            }
        }

        this.emojiAddingMode = false;
    }

    private unSelectEmojiResizeState() {
        for (const comp of this.drawingToolsComponents) {
            if (comp instanceof EmojiComponent) {
                comp.setComponentResizer(true);
            }
        }
    }

    public setPreselectEmoji(val: string) {
        this.preSelectedEmoji = val;
    }

    public hasPreselectEmoji(): boolean {
        return !!this.preSelectedEmoji;
    }

    public addRootDrawMode(emoji: string) {
        const highChartRoot = this.base.tiChartSeries.element.querySelector<HTMLElement>('.highcharts-root');
        if (highChartRoot) {
            highChartRoot.classList.add('highcharts-draw-mode');

            let defaultFontSize = this.base.tiChartSeries?.chartStyleSettings?.emojiDefaultSize ?? 24;

            if (!this.base.marketingWebChartMode) {
                defaultFontSize =  defaultFontSize * 2.2;
            } else {
                defaultFontSize =  defaultFontSize * 1.8;
            }

            let fontSize = (defaultFontSize * 0.6).toFixed(0);
            let width = defaultFontSize;
            let height = (defaultFontSize * 1.2).toFixed(0);

            let cursorX = (defaultFontSize / 2.3).toFixed(2);
            let cursorY = ((defaultFontSize * 1.2) / 4).toFixed(2);
            highChartRoot.setAttribute('style', `cursor:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'  width='${width}' height='${height}' viewport='0 0 100 100' style='fill:black;font-size:${fontSize}px;'><text y='50%'>${emoji}</text></svg>") ${cursorX} ${cursorY},auto !important;`);
            
            this.emojiAddingMode = true;
        }
    }

    private clearEmojiAddingModeTimer: any;
    public removeRootDrawMode(clearBeforeAdd: boolean) {
        const highChartRoot = this.base.tiChartSeries.element.querySelector<HTMLElement>('.highcharts-root');
        TraceLoggingHelpers.log('removeRootDrawMode 1', highChartRoot);
        if (highChartRoot) {
            highChartRoot.classList.remove('highcharts-draw-mode');
            highChartRoot.setAttribute('style', '');

            if (!clearBeforeAdd) {
                clearTimeout(this.clearEmojiAddingModeTimer);
                this.clearEmojiAddingModeTimer = setTimeout(() => {
                    TraceLoggingHelpers.log('removeRootDrawMode 2');
                    this.emojiAddingMode = false;
                }, 1000);
            }
        }
    }

    private subscribeDrawingTools() {
        TraceLoggingHelpers.log("subscribe called");
        this.base.chartService.subscribeInitChartDrawingTools((symbol: string, timeframe: Timeframe, data: any) => {

            this.symbol = symbol;
            this.timeframe = timeframe;
            this.cachedDrawingToolsToLoadJson = data;

            TraceLoggingHelpers.log('this.cachedDrawingToolsToLoadJson  : ', this.cachedDrawingToolsToLoadJson)
            this.sync(0);
        });
        this.base.chartService.subscribeInitChartDrawingToolSettings((data: DrawingToolsSettingModel) => {
            this.drawingToolsSetting = data;
        });
    }

    public sync(tries: number) {
        if (tries > 500) return;

        try {
            const chart: any = this.base.tiChartSeries?.highStockChart;
    
            if (!chart || !this.base.tiChartSeries.lastDataPoint) {
                return;
            }
    
            let shouldNotSync = this.base.tiChartSeries.preventDragging
                || this.emojiAddingMode
                || this.editAnnotaionPopupOpen
                || this.base.tiChartSeries.currentTimeframe != this.timeframe
                || this.base.tiChartSeries.symbol != this.symbol
                || this.base.tiChartSeries.chartStarting;
    
            TraceLoggingHelpers.log('shouldNotSync ' + shouldNotSync +
                '  this.emojiAddingMode ' + this.emojiAddingMode +
                '  this.base.tiChartSeries.preventDragging ' + this.base.tiChartSeries.preventDragging +
                ' this.editAnnotaionPopupOpen ' + this.editAnnotaionPopupOpen +
                ' this.base.tiChartSeries.currentTimeframe != this.timeframe  ' + (this.base.tiChartSeries.currentTimeframe != this.timeframe) +
                ' this.base.tiChartSeries.symbol != this.symbol  ' + (this.base.tiChartSeries.symbol != this.symbol)
            )
            if (shouldNotSync) {
                return;
            }
    
            // TraceLoggingHelpers.log('loadAnnotationFromCache ', this.drawingToolsComponents, this.cachedDrawingToolsToLoadJson)
    
            this.syncDelete();
    
            if (this.cachedDrawingToolsToLoadJson?.length) {
                for (const annotation of this.cachedDrawingToolsToLoadJson) {
                    const existingChartAnnotation = this.drawingToolsComponents.find((x: IDrawingTool) => x && x.id == annotation.id);
                    if (existingChartAnnotation) {
                        this.syncUpdate(existingChartAnnotation, annotation);
                    } else {
                        this.syncCreate(annotation);
                    }
                }
            }
        } catch (ex) { 
            TraceLoggingHelpers.log('Sync Error: ', ex);
            this.removeAllDrawingToolsComponents();
            this.sync(tries++);
        }
    }

    private syncCreate(annotationOptions: any) {
        const me = this;
        const series = this.base.tiChartSeries;
        const builder = this.base.tiChartSeries.highChartBuilder;

        let type = annotationOptions.langKey;
        if (annotationOptions.type != 'flags') {
            annotationOptions.events = {
                add: function (event: any) {
                    TraceLoggingHelpers.log('onAnnotationAdd add', event)
                    me.updateAnnotationStyles(event.target, annotationOptions, false);
                    series.onAnnotationAdd(event, true);
                },
                afterUpdate: function (event: any) {
                    me.updateAnnotationStyles(event.target, annotationOptions, true);
                    const afterDrawingToolCompleted = event && event.target && !event.target.isUpdating;
                    if (afterDrawingToolCompleted) {
                        if (event.target?.options?.typeOptions?.points?.length) {
                            for (const p of event.target.options.typeOptions.points) {
                                p.actualStartTimeT = null;
                                p.actualXValue = null;
                            }
                        }
    
                        TraceLoggingHelpers.log('saveAnnotations sync afterUpdate')
                        me.saveAnnotations();
                    }
                    builder.drawingToolRibbonUpdate(() => {
                        series.onAnnotationAfterUpdate(event);
                    });
                },
                remove: function () {
                    me.delete();
                }
            };
        } else {
            type = 'flags';
        }

        let component: IDrawingTool | null = null;
        if (type == DrawingToolsType.CurvedLine) {
            this.drawingToolsComponents.push(CurvedLineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.NewTextBox) {
            this.drawingToolsComponents.push(NewTextBoxComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.VerticalLabel && annotationOptions.isEmoji) {
            component = EmojiComponent.syncCreate(this.base.drawingToolsManager, annotationOptions)
        } else if (type == DrawingToolsType.Segment) {
            component = SegmentComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.ArrowSegment) {
            component = ArrowSegmentComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Ray) {
            component = RayComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Line) {
            component = LineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.ArrowLine) {
            component = ArrowLineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.HorizontaLine) {
            component = HorizontalLineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.VerticalLine) {
            component = VerticalLineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.ArrowRay) {
            component = ArrowRayComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Elliott3) {
            component = Elliot3LineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Elliott5) {
            component = Elliot5LineComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Crooked3) {
            component = Crooked3Component.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Crooked5) {
            component = Crooked5Component.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.VerticalCounter) {
            component = VerticalCounterComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.VerticalArrow) {
            component = VerticalArrowComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.VerticalLabel) {
            component = VerticalLabelComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.Fibonacci) {
            component = FibonacciComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        } else if (type == DrawingToolsType.FibonacciTimezones) {
            this.drawingToolsComponents.push(FibonacciTimezonesComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.Pitchfork) {
            this.drawingToolsComponents.push(PitchforkComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.ParallelChannel) {
            this.drawingToolsComponents.push(ParallelChannelComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.TimeCycles) {
            this.drawingToolsComponents.push(TimeCyclesComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.Measure) {
            this.drawingToolsComponents.push(MeasureComponent.syncCreate(this.base.drawingToolsManager, annotationOptions));
        } else if (type == DrawingToolsType.Flags) {
            component = FlagsComponent.syncCreate(this.base.drawingToolsManager, annotationOptions);
        }

        if (component) {
            this.drawingToolsComponents.push(component);
        }
    }

    // private customLabelsLastDataPointXValue: any = {};
    private syncUpdate(existingChartAnnotationComponent: IDrawingTool, annotationOptions: any) {
        if (!existingChartAnnotationComponent || !existingChartAnnotationComponent.annotation || !existingChartAnnotationComponent.annotation.options) {
            this.delete();
            return;
        }

        const existingChartAnnotation = existingChartAnnotationComponent.annotation;

        // TraceLoggingHelpers.log('update existingChartAnnotation ', existingChartAnnotation, annotationOptions)
        // if (annotationOptions.langKey == 'customLabel') {
            // const shouldUpdate = existingChartAnnotation.options.labels[0].point.x != annotationOptions.labels[0].point.x ||
            //     existingChartAnnotation.options.labels[0].point.y != annotationOptions.labels[0].point.y ||
            //     existingChartAnnotation.userOptions.labels[0].x != annotationOptions.labels[0].x ||
            //     existingChartAnnotation.userOptions.labels[0].y != annotationOptions.labels[0].y ||
            //     existingChartAnnotation.options.labelOptions.format != annotationOptions.labelOptions.format;

            // if (shouldUpdate) {
            //     existingChartAnnotation.update(annotationOptions);
            // } else {
            //     existingChartAnnotation.options.labelOptions.backgroundColor = annotationOptions.labelOptions.backgroundColor;
            //     existingChartAnnotation.options.labelOptions.style.fontSize = annotationOptions.labelOptions.style.fontSize;
            //     existingChartAnnotation.options.labelOptions.style.color = annotationOptions.labelOptions.style.color;

            //     existingChartAnnotation.userOptions.labelOptions.backgroundColor = annotationOptions.labelOptions.backgroundColor;
            //     if (existingChartAnnotation.userOptions.labelOptions.style && annotationOptions.labelOptions.style) {
            //         existingChartAnnotation.userOptions.labelOptions.style.fontSize = annotationOptions.labelOptions.style.fontSize;
            //         existingChartAnnotation.userOptions.labelOptions.style.color = annotationOptions.labelOptions.style.color;
            //     }
            // }

        //     this.updateAnnotationStyles(existingChartAnnotation, annotationOptions, false);
        // } else 
        if (annotationOptions.langKey == 'verticalLabel' && annotationOptions.isEmoji) {
            if (existingChartAnnotation.options.typeOptions) {
                let existingXValue = existingChartAnnotation.options.typeOptions.point.x;
                let newXValue = annotationOptions.typeOptions.point.x;
                
                if (annotationOptions.typeOptions?.point?.startTimeT > 0) {
                    const actualDataPoint = this.base.tiChartSeries.tiChartPoints.find(x => x.startTimeT == annotationOptions.typeOptions.point.startTimeT);

                    if (actualDataPoint) {
                        // const origialX = annotationOptions.typeOptions.point.originalX;
                        // const decimalPart = origialX - Math.floor(origialX);

                        newXValue = actualDataPoint.xValue;
                    }
                }

                // existingChartAnnotation.options.typeOptions.point.x = xValue;
                // existingChartAnnotation.options.typeOptions.point.startTimeT = annotationOptions.typeOptions?.point?.startTimeT;
                TraceLoggingHelpers.log('emoji update 1', existingChartAnnotation)

                let shouldUpdate =
                    existingXValue != newXValue ||
                    existingChartAnnotation.options.typeOptions.point.y != annotationOptions.typeOptions.point.y ||
                    existingChartAnnotation.options.typeOptions.connector.stroke != annotationOptions.typeOptions.connector.stroke ||
                    existingChartAnnotation.options.labelOptions.style.fontSize != annotationOptions.labelOptions.style.fontSize;

                if (shouldUpdate) {
                    annotationOptions.typeOptions.point.x = newXValue;

                    TraceLoggingHelpers.log('emoji update 2', annotationOptions)
                    existingChartAnnotation.update(annotationOptions);
                    this.updateAnnotationStyles(existingChartAnnotation, annotationOptions, true);
                }
            }
        } else {
            existingChartAnnotationComponent.syncUpdate(annotationOptions);
            // existingChartAnnotation.update(annotationOptions);
            // this.updateAnnotationStyles(existingChartAnnotation, annotationOptions, false);
        }
    }

    private syncDelete() {
        if (!this.drawingToolsComponents?.length) {
            return;
        }

        const removeIndexes: Array<number> = [];
        for (let i = 0; i < this.drawingToolsComponents.length; i++) {
            const drawingToolComponent = this.drawingToolsComponents[i];
            if (!this.cachedDrawingToolsToLoadJson.length || !this.cachedDrawingToolsToLoadJson.some((x: any) => x.id == drawingToolComponent.id)) {
                TraceLoggingHelpers.log('remove existingChartAnnotation ', drawingToolComponent)
                removeIndexes.push(i);
            }
        }

        for (const removeInd of removeIndexes) {
            if (this.drawingToolsComponents[removeInd] && this.drawingToolsComponents[removeInd].annotation?.options) {
                this.drawingToolsComponents[removeInd].annotation?.remove();
            }
        }

        for (const removeInd of removeIndexes) {
            this.drawingToolsComponents.splice(removeInd, 1);
        }
    }

    public removeAllDrawingToolsComponents() {
        TraceLoggingHelpers.log('removeAllDrawingToolsComponents ', this.drawingToolsComponents)
        for (let i = 0; i < this.drawingToolsComponents.length; i++) {
            const drawingToolComponent = this.drawingToolsComponents[i];
            TraceLoggingHelpers.log('remove existingChartAnnotation ', drawingToolComponent)

            try {
                if (drawingToolComponent && drawingToolComponent.annotation?.options) {
                    drawingToolComponent.annotation?.remove();
                }
            } catch {}
        }

        this.drawingToolsComponents = [];
    }

    public clearAll() {
        TraceLoggingHelpers.log('clearAll ', this.drawingToolsComponents)
        for (let i = 0; i < this.drawingToolsComponents.length; i++) {
            const drawingToolComponent = this.drawingToolsComponents[i];
            TraceLoggingHelpers.log('remove existingChartAnnotation ', drawingToolComponent)

            try {
                if (drawingToolComponent && drawingToolComponent.annotation?.options) {
                    drawingToolComponent.annotation?.remove();
                }
            } catch {}
        }

        this.drawingToolsComponents = [];
    }

    private removeTimer: any;
    public delete() {
        clearTimeout(this.removeTimer);
        this.removeTimer = setTimeout(() => {
            if (!this.drawingToolsComponents?.length) {
                return;
            }

            const removeIndexes: Array<number> = [];
            TraceLoggingHelpers.log('delete ', this.drawingToolsComponents)
            for (let i = 0; i < this.drawingToolsComponents.length; i++) {
                const drawingToolComponent = this.drawingToolsComponents[i];
                if (!drawingToolComponent) {
                    continue;
                }

                if (!drawingToolComponent.annotation || !drawingToolComponent.annotation.options || (drawingToolComponent instanceof FlagsComponent && !drawingToolComponent.annotation.options.data?.length)) {
                    TraceLoggingHelpers.log('remove existingChartAnnotation ', drawingToolComponent)
                    removeIndexes.push(i);
                }
            }

            for (const removeInd of removeIndexes) {
                if (removeInd > -1) {
                    this.drawingToolsComponents.splice(removeInd, 1);
                }
            }

            this.saveAnnotations();
        }, 100);
    }

    public updateAnnotationStyles(annotation: any, options: any, redraw: boolean) {
        const builder = this.base.tiChartSeries.highChartBuilder;
        TraceLoggingHelpers.log('updateAnnotationStyles annotation', annotation)
        TraceLoggingHelpers.log('updateAnnotationStyles options', options)
        TraceLoggingHelpers.log('updateAnnotationStyles redraw', redraw)
        TraceLoggingHelpers.log('updateAnnotationStyles found annotation.graphic?.element', annotation.graphic?.element)
        if (options.type == 'crookedLine' || options.type == 'infinityLine' || options.type == 'elliottWave') {
            annotation.graphic?.element?.querySelectorAll('path').forEach((el: Element) => {
                TraceLoggingHelpers.log('updateAnnotationStyles found el', el)

                if (!el.classList.contains('highcharts-tracker-line')) {
                    el.classList.add('colored');
                    el.setAttribute('stroke', options.typeOptions?.line?.stroke);
                    el.setAttribute('fill', options.typeOptions?.line?.fill);
                    el.setAttribute('stroke-width', options.typeOptions?.line?.strokeWidth);
                }
            });

            if (options.langKey == "curvedLine") {
                annotation['dtcl_prop'] = 'thisCurved';
                annotation.shapes.forEach(function (shape) {
                    shape.toD = function () {
                        var dOption = this.options.d;
                        if (dOption) {
                            return typeof dOption === 'function' ?
                                dOption.call(this) :
                                dOption;
                        }
                        var points = this.points,
                            len = points.length,
                            d = [];
                        var showPath = len,
                            point = points[0],
                            position = showPath && this.anchor(point).absolutePosition,
                            pointIndex = 0,
                            command;
                        if (position) {
                            d.push(['M', position.x, position.y]);
                            while (++pointIndex < len && showPath) {
                                point = points[pointIndex];
                                command = point.command || 'L';
                                position = this.anchor(point).absolutePosition;
                                if (command === 'M') {
                                    d.push([command, position.x, position.y]);
                                }
                                else if (command === 'L') {
                                    d.push([command, position.x, position.y]);
                                }
                                else if (command === 'Z') {
                                    d.push([command]);
                                }
                                showPath = point.series.visible;
                            }
                        }

                        var cline = builder.lineBetweenPoints([
                            [d[0][1], d[0][2]],
                            [d[1][1], d[1][2]],
                            [d[2][1], d[2][2]],
                        ]);
                        var cd = ("M," + cline.substring(1)).split(",");
                        d[0] = cd;
                        d[1] = ['M', d[2][2], d[2][2]];
                        d[2] = ['M', d[2][2], d[2][2]];

                        return (showPath && this.graphic ?
                            this.chart.renderer.crispLine(d, options.typeOptions?.line?.strokeWidth) :
                            null);
                    };
                });

                if (redraw) {
                    annotation.redraw();
                }
            }
        } else if (options.langKey == 'customLabel') {
            annotation.graphic?.element?.querySelectorAll('path').forEach((el: Element) => {
                el.setAttribute('style', `fill: ${options.labelOptions?.backgroundColor}; stroke: ${options.labelOptions?.backgroundColor};`);
            });
            annotation.graphic?.element?.querySelectorAll('rect').forEach((el: Element) => {
                el.setAttribute('style', `fill: ${options.labelOptions?.backgroundColor}; stroke: ${options.labelOptions?.backgroundColor};`);
            });
            annotation.graphic?.element?.querySelectorAll('text').forEach((el: Element) => {
                el.setAttribute('style', `font-size: ${options.labelOptions?.style?.fontSize}; fill: ${options.labelOptions?.style?.color};`);
            });
        } else if (options.langKey == 'verticalLabel' && options.isEmoji) {
            TraceLoggingHelpers.log(annotation, options)
            annotation.graphic?.element?.querySelectorAll('.highcharts-annotation-shapes').forEach((el: Element) => {
                el.setAttribute('style', `display: none`);
            });
            annotation.graphic?.element?.querySelectorAll('.highcharts-annotation-labels .highcharts-annotation-label-box').forEach((el: Element) => {
                el.setAttribute('style', `fill: transparent; stroke: transparent;`);
            });
            annotation.graphic?.element?.querySelectorAll('.highcharts-annotation-labels text').forEach((el: Element) => {
                let text = options.typeOptions?.connector?.stroke ?? '';
                // if (!EmojiComponent.validEmojiRegex.test(text)) {
                //     text = '';
                // }

                el.innerHTML = text;
                el.setAttribute('style', `font-size: ${options.labelOptions?.style?.fontSize}; fill: rgb(0, 0, 0);`);
            });

            if (redraw) {
                annotation.redraw();
            }
        } else if (options.type == 'basicAnnotation') {
            annotation.graphic?.element?.querySelectorAll('path').forEach((el: Element) => {
                el.setAttribute('style', `fill: ${options.labelOptions?.backgroundColor}; stroke: ${options.labelOptions?.backgroundColor};`);
            });
            annotation.graphic?.element?.querySelectorAll('text').forEach((el: Element) => {
                el.setAttribute('style', `font-size: ${options.labelOptions?.style?.fontSize}; fill: ${options.labelOptions?.style?.color};`);
            });
        }
    }
    
    public updatePreCreatedAnnotationStyles(annotation: any, updateOpt: boolean) {
        if (!annotation?.options || annotation.isEmoji) {
            return;
        }

        const type = annotation.options.langKey;
        const existingSetting = this.drawingToolsSetting.get(type);
        if (existingSetting) {
            const opt = existingSetting;
            if (updateOpt)
                annotation.update(opt);

            this.updateAnnotationStyles(annotation, annotation.options, false);
        }
    }

    public drawAnnotation(event: any) {
        const series = this.base.tiChartSeries;
        const builder = this.base.tiChartSeries.highChartBuilder;
        const chart: any = this.base.tiChartSeries?.highStockChart;
        let type = event.options.langKey;

        if (!series || !builder || !chart || !event?.options?.id || this.drawingToolsComponents.some((x: IDrawingTool) => x.id == event.options.id)) {
            return;
        }

        if (event.options.type == 'flags') {
            type = 'flags';
        }

        this.updatePreCreatedAnnotationStyles(event, true);

        // TraceLoggingHelpers.log('drawAnnotation ,', type, event)
        if (type == DrawingToolsType.Segment) {
            this.drawingToolsComponents.push(SegmentComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.ArrowSegment) {
            this.drawingToolsComponents.push(ArrowSegmentComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Ray) {
            this.drawingToolsComponents.push(RayComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Line) {
            this.drawingToolsComponents.push(LineComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.ArrowLine) {
            this.drawingToolsComponents.push(ArrowLineComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.HorizontaLine) {
            this.drawingToolsComponents.push(HorizontalLineComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.VerticalLine) {
            this.drawingToolsComponents.push(VerticalLineComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.ArrowRay) {
            this.drawingToolsComponents.push(ArrowRayComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Crooked3) {
            this.drawingToolsComponents.push(Crooked3Component.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Crooked5) {
            this.drawingToolsComponents.push(Crooked5Component.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Elliott3) {
            this.drawingToolsComponents.push(Elliot3LineComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Elliott5) {
            this.drawingToolsComponents.push(Elliot5LineComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.VerticalCounter) {
            this.drawingToolsComponents.push(VerticalCounterComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.VerticalArrow) {
            this.drawingToolsComponents.push(VerticalArrowComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.VerticalLabel) {
            this.drawingToolsComponents.push(VerticalLabelComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Fibonacci) {
            this.drawingToolsComponents.push(FibonacciComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.FibonacciTimezones) {
            this.drawingToolsComponents.push(FibonacciTimezonesComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Pitchfork) {
            this.drawingToolsComponents.push(PitchforkComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.ParallelChannel) {
            this.drawingToolsComponents.push(ParallelChannelComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.TimeCycles) {
            this.drawingToolsComponents.push(TimeCyclesComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Measure) {
            this.drawingToolsComponents.push(MeasureComponent.create(this.base.drawingToolsManager, event));
        } else if (type == DrawingToolsType.Flags) {
            this.drawingToolsComponents.push(FlagsComponent.create(this.base.drawingToolsManager, event));
        }

        TraceLoggingHelpers.log('drawAnnotation ,', this.drawingToolsComponents)
        this.saveAnnotations();
    }

    public drawCustomAnnotation(type: DrawingToolsType, event: any) {
        const series = this.base.tiChartSeries;
        const builder = this.base.tiChartSeries.highChartBuilder;
        const chart: any = this.base.tiChartSeries?.highStockChart;

        if (!series || !builder || !chart) {
            return;
        }

        // TraceLoggingHelpers.log('drawCustomAnnotation ,', type, event)
        
        let createdAnnotation: any = null;
        if (type == DrawingToolsType.CurvedLine) {
            const annotation = CurvedLineComponent.create(this.base.drawingToolsManager, event);
            this.drawingToolsComponents.push(annotation);

            createdAnnotation = annotation.annotation;
        } else if (type == DrawingToolsType.NewTextBox) {
            const annotation = NewTextBoxComponent.create(this.base.drawingToolsManager, event);
            this.drawingToolsComponents.push(annotation);

            createdAnnotation = annotation.annotation;
        } else if (type == DrawingToolsType.Emoji) {
            if (event.emojiType == 1 && !this.preSelectedEmoji) {
                return;
            }

            const annotation = EmojiComponent.create(this.base.drawingToolsManager, event, this.preSelectedEmoji, event.emojiType);
            this.drawingToolsComponents.push(annotation);

            createdAnnotation = annotation.annotation;
        }

        if (createdAnnotation)
            this.updatePreCreatedAnnotationStyles(createdAnnotation, true);

        this.preSelectedEmoji = '';

        TraceLoggingHelpers.log('drawCustomAnnotation ,', this.drawingToolsComponents)
        this.saveAnnotations();
    }

    public saveLastSettings(annotation: any) {
        TraceLoggingHelpers.log('saveSettings ', annotation)
        if (annotation) {
            this.drawingToolsSetting.update(annotation);
        }

        this.base.chartService.requestSaveDrawingToolSettings(this.drawingToolsSetting);
    }

    public saveAnnotations() {
        const dtMetaDeta: Array<any> = [];

        TraceLoggingHelpers.log('save annotations ', this.drawingToolsComponents)
        for (const drawingTool of this.drawingToolsComponents) {
            if (!drawingTool.annotation || !drawingTool.annotation.options) {
                continue;
            }

            const metaData = drawingTool.getMetaData();
            if (metaData) {

                TraceLoggingHelpers.log('saveAnnotations metaData ', metaData);
                // TraceLoggingHelpers.log('drawingTool.annotation ', drawingTool.annotation);

                // drawingTool.annotation
                // options
                //     look at the type, langKey, etc)

                // userOptions.typeOptions
                //     look at the type and what's under the points                 

                dtMetaDeta.push(metaData);
            }
        }

        this.base.chartService.requestSaveDrawingTools(dtMetaDeta);
    }
}